import React, { useEffect, useMemo, useState } from 'react';
import {
  exportPlatformUserActivityCsv,
  fetchPlatformUserActivityAnalytics,
  fetchPlatformUserActivitySession,
  fetchPlatformUserActivitySessions,
  type PlatformUserActivityAnalytics,
  type PlatformUserActivityDetail,
  type PlatformUserActivityList,
  type PlatformUserActivitySession
} from '../services/platformSupportUserActivity';
import './PlatformSupportUserActivityWorkspace.css';

function dateInputValue(date: Date) {
  return date.toISOString().slice(0, 10);
}

function formatDateTime(value: string | null | undefined) {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }).format(new Date(value));
}

function formatDuration(secondsInput: number | null | undefined) {
  const seconds = Math.max(0, Math.round(Number(secondsInput || 0)));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${seconds}s`;
}

function userLabel(session: PlatformUserActivitySession) {
  return session.user.fullName || session.user.username;
}

function locationLabel(session: PlatformUserActivitySession) {
  const approximate = [session.location.city, session.location.region, session.location.countryCode].filter(Boolean).join(', ');
  return approximate || session.location.timezone || 'Location unavailable';
}

function formatShortDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(new Date(`${value}T00:00:00.000Z`));
}

const PlatformSupportUserActivityWorkspace: React.FC<{ token: string }> = ({ token }) => {
  const initialFrom = useMemo(() => {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() - 30);
    return dateInputValue(date);
  }, []);
  const [data, setData] = useState<PlatformUserActivityList | null>(null);
  const [analytics, setAnalytics] = useState<PlatformUserActivityAnalytics | null>(null);
  const [busy, setBusy] = useState(false);
  const [analyticsBusy, setAnalyticsBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [userId, setUserId] = useState('');
  const [status, setStatus] = useState('ALL');
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(() => dateInputValue(new Date()));
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PlatformUserActivityDetail | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setBusy(true);
    void fetchPlatformUserActivitySessions(token, { page, pageSize: 25, search, organizationId, userId, status, from, to })
      .then(setData)
      .catch(caught => setError(caught instanceof Error ? caught.message : 'User activity sessions could not be loaded'))
      .finally(() => setBusy(false));
  }, [from, organizationId, page, refreshKey, search, status, to, token, userId]);

  useEffect(() => {
    setAnalyticsBusy(true);
    void fetchPlatformUserActivityAnalytics(token, { search, organizationId, userId, status, from, to })
      .then(setAnalytics)
      .catch(caught => setError(caught instanceof Error ? caught.message : 'User activity reporting could not be loaded'))
      .finally(() => setAnalyticsBusy(false));
  }, [from, organizationId, refreshKey, search, status, to, token, userId]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setDetailBusy(true);
    void fetchPlatformUserActivitySession(token, selectedId)
      .then(setDetail)
      .catch(caught => setError(caught instanceof Error ? caught.message : 'Session detail could not be loaded'))
      .finally(() => setDetailBusy(false));
  }, [selectedId, token]);

  useEffect(() => {
    const timer = window.setInterval(() => setRefreshKey(value => value + 1), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const pagination = data?.pagination;
  const maxDailyActiveSeconds = Math.max(0, ...(analytics?.daily || []).map(day => day.activeSeconds));

  const exportCsv = async () => {
    setExporting(true);
    try {
      const exported = await exportPlatformUserActivityCsv(token, { search, organizationId, userId, status, from, to });
      const href = window.URL.createObjectURL(exported.blob);
      const link = document.createElement('a');
      link.href = href;
      link.download = exported.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(href);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'User activity export could not be completed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="platform-activity-workspace">
      <div className="platform-activity-heading">
        <div>
          <div className="platform-activity-eyebrow">BONZAIQ OPERATIONS</div>
          <h1>User activity</h1>
          <p>Review product logins, active use, approximate network location, and page visits across UNIT and ORG accounts.</p>
        </div>
        <button type="button" className="platform-activity-refresh" onClick={() => setRefreshKey(value => value + 1)} disabled={busy}>
          {busy ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="platform-activity-summary">
        <div><strong>{data?.summary.active ?? 0}</strong><span>Active now</span></div>
        <div><strong>{data?.summary.inactive ?? 0}</strong><span>Inactive sessions</span></div>
        <div><strong>{data?.summary.ended ?? 0}</strong><span>Signed out</span></div>
        <div><strong>{formatDuration(data?.summary.averageActiveSeconds)}</strong><span>Average active time</span></div>
      </div>

      <section className="platform-activity-filters" aria-label="User activity filters">
        <form className="platform-activity-search" onSubmit={event => { event.preventDefault(); setSearch(searchDraft.trim()); setPage(1); }}>
          <label htmlFor="platform-activity-search-input">Search</label>
          <div>
            <input id="platform-activity-search-input" value={searchDraft} onChange={event => setSearchDraft(event.target.value)} placeholder="User, UNIT, IP, or page" />
            <button type="submit" disabled={busy}>Search</button>
            {(search || searchDraft) ? <button type="button" onClick={() => { setSearchDraft(''); setSearch(''); setPage(1); }}>Clear</button> : null}
          </div>
        </form>
        <label>UNIT / ORG
          <select value={organizationId} onChange={event => {
            const nextOrganizationId = event.target.value;
            const selectedUser = data?.filterOptions.users.find(user => user.id === userId);
            setOrganizationId(nextOrganizationId);
            if (selectedUser && nextOrganizationId && !selectedUser.organizationIds.includes(nextOrganizationId)) setUserId('');
            setPage(1);
          }}>
            <option value="">All accounts</option>
            {(data?.filterOptions.organizations || []).map(organization => <option key={organization.id} value={organization.id}>{organization.name || organization.slug || organization.id}</option>)}
          </select>
        </label>
        <label>User
          <select value={userId} onChange={event => {
            const nextUserId = event.target.value;
            const selectedUser = data?.filterOptions.users.find(user => user.id === nextUserId);
            setUserId(nextUserId);
            if (selectedUser?.organizationIds.length === 1) setOrganizationId(selectedUser.organizationIds[0]);
            else if (selectedUser && organizationId && !selectedUser.organizationIds.includes(organizationId)) setOrganizationId('');
            setPage(1);
          }}>
            <option value="">All users</option>
            {(data?.filterOptions.users || []).map(user => <option key={user.id} value={user.id}>{user.fullName || user.username} (@{user.username})</option>)}
          </select>
        </label>
        <label>Status
          <select value={status} onChange={event => { setStatus(event.target.value); setPage(1); }}>
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active now</option>
            <option value="INACTIVE">Inactive</option>
            <option value="ENDED">Signed out</option>
          </select>
        </label>
        <label>From<input type="date" value={from} onChange={event => { setFrom(event.target.value); setPage(1); }} /></label>
        <label>To<input type="date" value={to} onChange={event => { setTo(event.target.value); setPage(1); }} /></label>
        {(organizationId || userId) ? <button type="button" className="platform-activity-clear-filters" onClick={() => { setOrganizationId(''); setUserId(''); setPage(1); }}>Clear account filters</button> : null}
      </section>

      <section className="platform-activity-report" aria-label="Filtered user activity report">
        <div className="platform-activity-report-heading">
          <div><strong>Usage report</strong><span>All reporting below follows the selected search, account, user, status, and date filters.</span></div>
          <button type="button" onClick={() => void exportCsv()} disabled={exporting || analyticsBusy}>{exporting ? 'Exportingâ€¦' : 'Export CSV'}</button>
        </div>
        <div className="platform-activity-report-totals">
          <div><strong>{analytics?.totals.sessions ?? 0}</strong><span>Filtered sessions</span></div>
          <div><strong>{analytics?.totals.uniqueUsers ?? 0}</strong><span>Unique users</span></div>
          <div><strong>{formatDuration(analytics?.totals.totalActiveSeconds)}</strong><span>Active session time</span></div>
          <div><strong>{formatDuration(analytics?.totals.totalSessionSeconds)}</strong><span>Total session time</span></div>
          <div><strong>{formatDuration(analytics?.totals.averageActiveSessionSeconds)}</strong><span>Average active session time</span></div>
          <div><strong>{formatDuration(analytics?.totals.averageSessionSeconds)}</strong><span>Average session time</span></div>
        </div>
        <div className="platform-activity-report-grid">
          <article className="platform-activity-report-card platform-activity-trend-card">
            <div className="platform-activity-card-heading"><strong>Active-use trend</strong><span>Active time by session start date</span></div>
            {analyticsBusy && !analytics ? <div className="platform-activity-empty">Loading usage reportâ€¦</div> : (analytics?.daily.length || 0) > 0 ? (
              <div className="platform-activity-chart-scroll">
                <div className="platform-activity-chart" style={{ minWidth: `${Math.max(520, (analytics?.daily.length || 0) * 34)}px` }}>
                  {(analytics?.daily || []).map(day => <div className="platform-activity-chart-day" key={day.date} title={`${formatShortDate(day.date)}: ${formatDuration(day.activeSeconds)} active, ${day.sessions} sessions, ${day.uniqueUsers} users`}>
                    <span>{day.activeSeconds ? formatDuration(day.activeSeconds) : ''}</span>
                    <div><i style={{ height: maxDailyActiveSeconds ? `${Math.max(4, Math.round(day.activeSeconds / maxDailyActiveSeconds * 100))}%` : '0%' }} /></div>
                    <small>{formatShortDate(day.date)}</small>
                  </div>)}
                </div>
              </div>
            ) : <div className="platform-activity-empty">No activity is available for this period.</div>}
          </article>
          <article className="platform-activity-report-card">
            <div className="platform-activity-card-heading"><strong>Top pages</strong><span>Ranked by active time</span></div>
            <div className="platform-activity-ranking">
              {(analytics?.pages || []).slice(0, 8).map(pageItem => <div key={`${pageItem.surface}:${pageItem.pageKey}`}><span><strong>{pageItem.pageLabel}</strong><small>{pageItem.visitCount} visits</small></span><b>{formatDuration(pageItem.activeSeconds)}</b></div>)}
              {!analyticsBusy && !(analytics?.pages.length) ? <div className="platform-activity-empty">No page usage recorded.</div> : null}
            </div>
          </article>
          <article className="platform-activity-report-card">
            <div className="platform-activity-card-heading"><strong>Most active accounts</strong><span>Login account attribution</span></div>
            <div className="platform-activity-ranking">
              {(analytics?.organizations || []).slice(0, 8).map(item => <div key={item.organization.id}><span><strong>{item.organization.name || item.organization.slug}</strong><small>{item.sessionCount} sessions</small></span><b>{formatDuration(item.activeSeconds)}</b></div>)}
              {!analyticsBusy && !(analytics?.organizations.length) ? <div className="platform-activity-empty">No account usage recorded.</div> : null}
            </div>
          </article>
          <article className="platform-activity-report-card">
            <div className="platform-activity-card-heading"><strong>Most active users</strong><span>Ranked by active time</span></div>
            <div className="platform-activity-ranking">
              {(analytics?.users || []).slice(0, 8).map(item => <div key={item.user.id}><span><strong>{item.user.fullName || item.user.username}</strong><small>@{item.user.username} Â· {item.sessionCount} sessions</small></span><b>{formatDuration(item.activeSeconds)}</b></div>)}
              {!analyticsBusy && !(analytics?.users.length) ? <div className="platform-activity-empty">No user activity recorded.</div> : null}
            </div>
          </article>
        </div>
      </section>

      <section className="platform-activity-list" aria-label="User activity sessions">
        <div className="platform-activity-list-heading">
          <div><strong>{pagination?.total ?? 0} sessions</strong><span>Telemetry is retained for {data?.retentionDays ?? 90} days.</span></div>
          {pagination && pagination.total > 0 ? <span>{(pagination.page - 1) * pagination.pageSize + 1}–{Math.min(pagination.total, pagination.page * pagination.pageSize)} of {pagination.total}</span> : null}
        </div>
        <div className="platform-activity-table-wrap">
          <table className="platform-activity-table">
            <thead><tr><th>User / account</th><th>Status</th><th>Started / duration</th><th>Active time</th><th>Last page</th><th>Location / device</th><th aria-label="Open session" /></tr></thead>
            <tbody>
              {(data?.records || []).map(session => (
                <React.Fragment key={session.id}>
                  <tr className={selectedId === session.id ? 'is-selected' : undefined}>
                    <td><strong>{userLabel(session)}</strong><span>@{session.user.username} · {session.organization.name || session.organization.slug}</span><small>{session.roleKey || 'Unknown role'} · {session.licenseProductCode || session.licenseAccessType || 'Licence not identified'}</small></td>
                    <td><span className={`platform-activity-status is-${session.status.toLowerCase()}`}>{session.status === 'ENDED' ? 'SIGNED OUT' : session.status}</span></td>
                    <td><strong>{formatDateTime(session.startedAt)}</strong><span>{formatDuration(session.durationSeconds)}</span></td>
                    <td><strong>{formatDuration(session.activeSeconds)}</strong><span>Last seen {formatDateTime(session.lastSeenAt)}</span></td>
                    <td><strong>{session.currentPage?.label || 'No page reported'}</strong><span>{session.pageVisitCount || 0} page intervals</span></td>
                    <td><strong>{locationLabel(session)}</strong><span>{session.location.ipAddress || 'IP unavailable'}</span><small>{[session.device.type, session.device.browser, session.device.os].filter(Boolean).join(' · ') || 'Device unavailable'}</small></td>
                    <td><button type="button" className="platform-activity-open" onClick={() => setSelectedId(current => current === session.id ? null : session.id)}>{selectedId === session.id ? 'Close' : 'Timeline'}</button></td>
                  </tr>
                  {selectedId === session.id ? (
                    <tr className="platform-activity-detail-row"><td colSpan={7}>
                      {detailBusy || detail?.id !== session.id ? <div className="platform-activity-empty">Loading page timeline…</div> : detail.pageVisits.length ? (
                        <div className="platform-activity-timeline">
                          {detail.pageVisits.map(visit => <div key={visit.id}>
                            <span className="platform-activity-timeline-marker" />
                            <div><strong>{visit.pageLabel}</strong><span>{formatDateTime(visit.startedAt)} – {formatDateTime(visit.endedAt || visit.lastSeenAt)}</span><small>{formatDuration(visit.durationSeconds)} open · {formatDuration(visit.activeSeconds)} active</small></div>
                          </div>)}
                        </div>
                      ) : <div className="platform-activity-empty">No page visits were recorded for this session.</div>}
                    </td></tr>
                  ) : null}
                </React.Fragment>
              ))}
              {!busy && (data?.records.length || 0) === 0 ? <tr><td colSpan={7}><div className="platform-activity-empty">No sessions match these filters.</div></td></tr> : null}
            </tbody>
          </table>
        </div>
        {pagination && pagination.totalPages > 1 ? <div className="platform-activity-pagination">
          <button type="button" disabled={pagination.page <= 1 || busy} onClick={() => setPage(value => Math.max(1, value - 1))}>Previous</button>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
          <button type="button" disabled={pagination.page >= pagination.totalPages || busy} onClick={() => setPage(value => value + 1)}>Next</button>
        </div> : null}
      </section>

      {error ? <div className="platform-activity-error-backdrop" role="presentation">
        <section role="alertdialog" aria-modal="true" aria-labelledby="platform-activity-error-title" className="platform-activity-error-dialog">
          <h2 id="platform-activity-error-title">User activity could not be loaded</h2>
          <p>{error}</p>
          <button type="button" onClick={() => setError(null)} autoFocus>Close</button>
        </section>
      </div> : null}
    </div>
  );
};

export default PlatformSupportUserActivityWorkspace;
